import React, { useState, useEffect, useMemo } from 'react'
import { format, differenceInSeconds } from 'date-fns'
import { get, map } from 'lodash-es'
import { subscribe } from 'react-contextual'
import { stringUtils } from '../../../../../utils'
import { PaymentsProvider, SearchFilterProvider } from '../../../../providers'
import {
  AggregateTable,
  Header,
  CurrencyText,
  Label,
  Tooltip,
  Timer,
  DateRangePicker,
  NoContent,
  Loader,
} from '../../..'
import InvoiceIcon from '../../../atoms/Svgs/Invoice'
import PaymentsAltIcon from '../../../atoms/Svgs/PaymentsAlt'
import WithdrawIcon from '../../../atoms/Svgs/Withdraw'
import styles from './PaymentsTable.module.scss'
import { pathOr, splitAt } from 'ramda'
import { $TSFixMe } from 'types/ts-migrate'

interface PaymentsTableProps {
  paymentFilters: $TSFixMe
  paymentPeriod: { from?: string; to?: string }
  searchQuery: string
  activeFilterIndex: number
  getPayments: (from: string, to: string) => void
  isPaymentsRefreshing: boolean
}

const PaymentsTable = ({
  paymentFilters,
  paymentPeriod,
  searchQuery,
  activeFilterIndex,
  getPayments,
  isPaymentsRefreshing,
}: PaymentsTableProps): React.ReactElement => {
  const [data, setData] = useState([])
  const [startDate, setStartDate] = useState(paymentPeriod.from ? new Date(paymentPeriod.from) : new Date())
  const [endDate, setEndDate] = useState(paymentPeriod.to ? new Date(paymentPeriod.to) : new Date())
  const [mouseOverItem, setMouseOverItem] = useState(null)
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const filterByType = get(paymentFilters, `[${[activeFilterIndex]}]`, {})

  useEffect(() => {
    setData(filterByType.payments)
  }, [activeFilterIndex, filterByType, startDate, endDate])

  const filteredPayments = useMemo(() => {
    return data.filter(inv => stringUtils.jsonStringSearch(searchQuery, inv))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  const getButtonColour = (status: any): string => {
    switch (status) {
      case 'Queue':
        return '#056dba'
      case 'Verified':
        return '#128269'
      case 'Approved':
      case 'Requested':
      case 'Demanded':
      case 'AwaitingTransfer':
        return '#403f3f'
      case 'Reversed':
      case 'WalletRejected':
      case 'BankRejected':
      case 'RequestRejected':
        return '#eb0018'
      default:
        return '#056dba'
    }
  }

  const getButtonLabelAlias = (status: any): string => {
    switch (status) {
      case 'Demanded':
        return 'Submitted'
      case 'BankRejected':
        return 'Bank Rejected'
      case 'WalletRejected':
        return 'Wallet Rejected'
      case 'RequestRejected':
        return 'Request Rejected'
      default:
        return status
    }
  }

  const getSecondsToSubmission = (payoutAt: any): number => {
    const countdownTime = differenceInSeconds(payoutAt, new Date())
    return countdownTime > 0 ? countdownTime : 0
  }

  const columns = [
    {
      Header: 'Type',
      accessor: 'paymentType',
      style: {
        maxWidth: '300px',
        display: 'flex',
        flexDirection: 'row',
      },
    },
    {
      Header: 'To',
      accessor: 'to',
      style: {
        maxWidth: '300px',
        display: 'flex',
        flexDirection: 'row',
      },
    },
    {
      Header: 'From',
      accessor: 'from',
      style: {
        justifyContent: 'center',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    {
      Header: 'Approved',
      accessor: 'submittedAt',
      style: {
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '125px',
        alignItems: 'flex-start',
      },
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      style: {
        display: 'flex',
        justifyContent: 'flex-end',
        maxWidth: '150px',
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      style: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '20px',
        maxWidth: '150px',
      },
    },
  ]

  const paymentKindRenderer = (paymentKind: string, paymentIndicator: string) => {
    switch (`${paymentKind}:${paymentIndicator}`) {
      case 'EFTPartyPayment:IIP':
        return 'Instant Payment'
      case 'EFTPartyPayment:PAAF':
      case 'EFTPartyPayment:None':
        return 'Standard Payment'
      case 'DepositRefund:IIP':
        return 'Instant Deposit Refund'
      case 'DepositRefund:PAAF':
        return 'Standard Deposit Refund'
      case 'DepositRefund:None':
        return 'Deposit Refund'
      case 'WalletTransfer:None':
        return 'Wallet Transfer'
      case 'DepositPayment:IIP':
      case 'DepositPayment:PAAF':
      case 'DepositPayment:None':
        return 'Deposit Transfer In'
      case 'DepositTransfer:IIP':
      case 'DepositTransfer:PAAF':
      case 'DepositTransfer:None':
        return 'Deposit Transfer Out'
      case 'EasypayPayment:IIP':
      case 'EasypayPayment:PAAF':
      case 'EasypayPayment:None':
        return 'Lesaka EasyPay Payment'
      default:
        return paymentKind.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    }
  }

  const paymentKindClass = (paymentKind: string, paymentIndicator: string): string => {
    switch (`${paymentKind}:${paymentIndicator}`) {
      case 'EFTPartyPayment:IIP':
        return styles['instant-payment']
      case 'DepositRefund:IIP':
        return styles['instant-deposit-refund']
      default:
        return 'payment-kind'
    }
  }

  const getTransactionId = (id: any): string => pathOr('', [0], splitAt(6, id))

  const paymentsData = useMemo(() => {
    const buildPaymentsObj = (payment: any): $TSFixMe => ({
      id: payment.id,
      invoiceNumber: payment.invoiceNumber,
      enableSelection: payment.enableSelection,

      paymentType: (
        <>
          {payment.paymentKind.type === 'WalletTransfer' ||
          payment.paymentKind.type === 'DepositPayment' ||
          payment.paymentKind.type === 'DepositTransfer' ? (
            <PaymentsAltIcon title="Wallet Transfer" />
          ) : (
            <WithdrawIcon title="EFT" />
          )}
          <span className={styles['payment-type-wrapper']}>
            <strong className={paymentKindClass(payment.paymentKind.type, payment.paymentIndicator)}>
              {paymentKindRenderer(
                payment.paymentKind.type,
                payment.paymentIndicator ? payment.paymentIndicator : 'None',
              )}
            </strong>
            <small className={styles['invoice-number']}>
              {payment.invoiceNumber && `INV-${payment.invoiceNumber}`}
            </small>
            <small className={styles['transaction-id']}>Transaction ID: {getTransactionId(payment.id)}</small>
          </span>
        </>
      ),

      to: (
        <>
          <span>
            <strong>{payment.paymentKind.partyFullname || payment.paymentKind.recipientName}</strong>
            {payment.paymentKind.bankName &&
            payment.paymentKind.bankAccountLastFour &&
            (payment.paymentKind.type === 'EFTPartyPayment' || payment.paymentKind.type === 'DepositRefund') ? (
              <small className={styles['bank-details']}>
                {payment.paymentKind.bankName}
                &#x2a;&#x2a;&#x2a;&#x2a;&#x2a;
                {payment.paymentKind.bankAccountLastFour}
              </small>
            ) : null}
            {payment.paymentKind.easypayReference ? (
              <small className={styles['reference']}>Reference: {payment.paymentKind.easypayReference}</small>
            ) : payment.paymentKind.type === 'WalletTransfer' || payment.paymentKind.type === 'DepositRefund' ? (
              <small className={styles['reference']}>Reference: {payment.paymentKind.reference}</small>
            ) : payment.paymentKind.bankReference &&
              (payment.paymentKind.type === 'EFTPartyPayment' ||
                payment.paymentKind.type === 'EasypayPayment' ||
                payment.paymentKind.type === 'DepositRefund') ? (
              <small className={styles['reference']}>Reference: {payment.paymentKind.bankReference}</small>
            ) : null}
          </span>
        </>
      ),

      from: (
        <>
          <span>
            <strong>{payment.fromFullname}</strong>
            <div
              style={{
                textOverflow: 'ellipsis',
                width: '100%',
                overflow: 'hidden',
                fontSize: 'smaller',
              }}
            >
              {payment.fromProperty}
            </div>
          </span>
        </>
      ),

      submittedAt: (
        <div
          style={{
            textOverflow: 'ellipsis',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <small>
            {format(payment.approvedAt, 'yyyy-MM-dd')}
            <br />
            {format(payment.approvedAt, 'HH:mm:ss')}
            <br />
            {payment.approvedByFullname === 'NA' ? 'System' : payment.approvedByFullname}
          </small>
        </div>
      ),

      amount: (
        <div style={{ textAlign: 'right' }}>
          <CurrencyText>{payment.amount}</CurrencyText>
        </div>
      ),

      status: (
        <Tooltip
          place="left"
          body={
            <span>
              Payment will be submitted in <Timer duration={getSecondsToSubmission(payment.payoutAt)} />
            </span>
          }
          isOpen={payment.paymentStatus === 'Queue' && mouseOverItem === payment.id}
        >
          <Label
            className={styles['status-label']}
            style={{ backgroundColor: getButtonColour(payment.paymentStatus) }}
            onMouseOver={() => setMouseOverItem(payment.id)}
            onMouseLeave={() => setMouseOverItem(null)}
          >
            {getButtonLabelAlias(payment.paymentStatus)}
          </Label>
        </Tooltip>
      ),
    })

    return searchQuery ? map(filteredPayments, buildPaymentsObj) : map(data, buildPaymentsObj)
  }, [data, filteredPayments, searchQuery, mouseOverItem])

  return useMemo(
    () => (
      <div className={styles.root}>
        <div className={styles['header-wrapper']}>
          <Header icon={<InvoiceIcon />} text="Payments" />
          <span className={styles['datepicker-wrapper']}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={(date: any, isStartDate: any) => {
                if (date) {
                  if (isStartDate) {
                    setStartDate(date)
                    getPayments(format(date, 'yyyy-MM-dd'), format(endDate, 'yyyy-MM-dd'))
                  } else {
                    setEndDate(date)
                    getPayments(format(startDate, 'yyyy-MM-dd'), format(date, 'yyyy-MM-dd'))
                  }
                }
              }}
              inline={false}
            />
          </span>
        </div>

        <div className={styles['table-wrapper']}>
          {isPaymentsRefreshing && (
            <div className={styles['refresh-overlay']}>
              <Loader />
            </div>
          )}
          {paymentsData.length > 0 ? (
            <AggregateTable
              enableSelection
              hideHeaderSelection
              columns={columns}
              data={paymentsData}
              rowHeight={80}
              interactive={false}
            />
          ) : (
            <NoContent heading="There are no payments yet." />
          )}
        </div>
      </div>
    ),
    [columns, paymentsData, startDate, endDate, getPayments, isPaymentsRefreshing],
  )
}

export default subscribe(
  [PaymentsProvider, SearchFilterProvider],
  (
    { paymentFilters, paymentPeriod, getPayments, isPaymentsRefreshing }: any,
    { searchQuery, activeFilterIndex }: any,
  ) => ({
    paymentFilters,
    paymentPeriod,
    isPaymentsRefreshing,
    searchQuery,
    activeFilterIndex,
    getPayments,
  }),
)(PaymentsTable)
